/**
 * Hero carousel behaviour (ADR-0006) — progressive enhancement, vanilla JS, no
 * library.
 *
 * The hero is fully usable without JS: the `.c-hero__track` is a horizontally
 * scrollable, scroll-snapping flex row, so a visitor can swipe/scroll between
 * slides and the first slide is visible on load. This script layers on:
 *
 *   - prev/next buttons that scroll the track by exactly one slide width,
 *   - pagination dots that reflect the active slide and scroll to it on click,
 *   - the active dot kept in sync as the track is scrolled (IntersectionObserver
 *     over the slides, with a scroll-position fallback),
 *   - left/right arrow-key navigation while the carousel (track or a control)
 *     holds focus,
 *   - `prefers-reduced-motion` honoured: jumps are instant, never smooth.
 *
 * NO autoplay and NO looping (deliberate — see ADR-0006). Everything self-guards
 * on DOM-ready and degrades gracefully when expected elements are absent (e.g. a
 * single-slide hero renders no nav/dots, so this script simply does nothing for
 * it).
 *
 * `js-` hooks (js-hero-track, js-hero-prev, js-hero-next, js-hero-dots,
 * js-hero-dot) are behaviour-only and are never styled.
 */
(function () {
  'use strict';

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function initHero(hero) {
    var track = hero.querySelector('.js-hero-track');
    if (!track) {
      return;
    }

    var slides = Array.prototype.slice.call(
      track.querySelectorAll('.c-hero__slide')
    );
    if (slides.length < 2) {
      // A single (or empty) hero is a static banner — nothing to drive.
      return;
    }

    var prev = hero.querySelector('.js-hero-prev');
    var next = hero.querySelector('.js-hero-next');
    var dots = Array.prototype.slice.call(
      hero.querySelectorAll('.js-hero-dot')
    );

    var current = 0;

    function behavior() {
      return prefersReducedMotion() ? 'auto' : 'smooth';
    }

    function clamp(index) {
      return Math.max(0, Math.min(index, slides.length - 1));
    }

    // Scroll the track so the slide at `index` aligns to the start. Using the
    // slide's own offset keeps this correct regardless of gaps or padding.
    function goTo(index) {
      var target = clamp(index);
      var slide = slides[target];
      if (!slide) {
        return;
      }
      track.scrollTo({ left: slide.offsetLeft, behavior: behavior() });
      setActive(target);
    }

    function setActive(index) {
      current = clamp(index);
      dots.forEach(function (dot, i) {
        var isActive = i === current;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    // --- Controls ------------------------------------------------------------
    if (prev) {
      prev.addEventListener('click', function () {
        goTo(current - 1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        goTo(current + 1);
      });
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        goTo(i);
      });
    });

    // --- Keyboard ------------------------------------------------------------
    // Arrow keys page through the carousel while it (or one of its controls)
    // holds focus. Left/right only — vertical scroll stays the browser's.
    hero.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(current - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(current + 1);
      }
    });

    // --- Active-slide sync on scroll ----------------------------------------
    // Prefer IntersectionObserver: whichever slide is most visible within the
    // track wins. Falls back to a scroll-position computation when IO is absent.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          var best = null;
          entries.forEach(function (entry) {
            if (
              entry.isIntersecting &&
              (!best || entry.intersectionRatio > best.intersectionRatio)
            ) {
              best = entry;
            }
          });
          if (best) {
            var index = slides.indexOf(best.target);
            if (index !== -1) {
              setActive(index);
            }
          }
        },
        { root: track, threshold: [0.5, 0.75, 1] }
      );
      slides.forEach(function (slide) {
        io.observe(slide);
      });
    } else {
      var scrollTicking = false;
      track.addEventListener(
        'scroll',
        function () {
          if (scrollTicking) {
            return;
          }
          scrollTicking = true;
          window.requestAnimationFrame(function () {
            var index = Math.round(track.scrollLeft / track.clientWidth);
            setActive(index);
            scrollTicking = false;
          });
        },
        { passive: true }
      );
    }

    // Initialise the dot state (first slide active on load).
    setActive(0);
  }

  function init() {
    var heroes = document.querySelectorAll('.c-hero');
    Array.prototype.forEach.call(heroes, initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
